import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import styles from './CategoriesView.module.css';
import { Edit, Plus, Search, Trash, Globe, ArrowUp, ArrowDown } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useCategories } from '../../../../hooks/categories/useCategories';
import Loading from '../../../../common/HousGlass Loader/Loading';

const LANGUAGES = [
  { value: 'english', label: 'English' },
  { value: 'hindi', label: 'Hindi' },
  { value: 'garhwali', label: 'Garhwali' },
  { value: 'kumaoni', label: 'Kumaoni' },
  { value: 'marathi', label: 'Marathi' }
];

const CategoryModal = ({ isOpen, onClose, title, categoryData = { name: '', language: 'english', rank: 0, pageTitle: '' , metaKeywords: '' , metaDescription: '' }, onSubmit }) => {
  const [name, setName] = useState(categoryData?.name || '');
  const [language, setLanguage] = useState(categoryData?.language || 'english');
  const [rank, setRank] = useState(categoryData?.rank || 0);
  const [customSlug, setCustomSlug] = useState(categoryData?.slug || '');
  const [pageTitle, setPageTitle] = useState(categoryData?.page_title || '');
  const [metaKeywords, setMetaKeywords] = useState(categoryData?.meta_keywords || '');
  const [metaDescription, setMetaDescription] = useState(categoryData?.meta_description || '');


  // Generate slug preview
  const generateSlug = (name) => {
    return name
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const slugPreview = generateSlug(name);

  // Validate custom slug format
  const validateSlug = (slug) => {
    if (!slug.trim()) return { isValid: true, message: '' };
    
    const slugRegex = /^[a-z0-9-]+$/;
    if (!slugRegex.test(slug)) {
      return { isValid: false, message: 'Only lowercase letters, numbers, and hyphens allowed' };
    }
    
    if (slug.startsWith('-') || slug.endsWith('-')) {
      return { isValid: false, message: 'Cannot start or end with hyphen' };
    }
    
    if (slug.includes('--')) {
      return { isValid: false, message: 'Cannot contain consecutive hyphens' };
    }
    
    return { isValid: true, message: 'Valid slug format' };
  };

  const slugValidation = validateSlug(customSlug);

  // Update form when category data changes
  useEffect(() => {
    if (categoryData) {
      setName(categoryData.name || '');
      setLanguage(categoryData.language || 'english');
      setRank(categoryData.rank !== undefined ? categoryData.rank : 0);
      setCustomSlug(categoryData.slug || '');
      setPageTitle(categoryData.page_title || '');
      setMetaKeywords(categoryData.meta_keywords || '');
      setMetaDescription(categoryData.meta_description || '');
    }
  }, [categoryData]);

  // Auto-fill slug when name changes (only if custom slug is empty or matches the old auto-generated slug)
  useEffect(() => {
    if (!customSlug || customSlug === generateSlug(categoryData?.name || '')) {
      setCustomSlug(slugPreview);
    }
  }, [name, categoryData?.name]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    // Use custom slug if provided, otherwise use auto-generated slug
    let finalSlug = customSlug.trim() || slugPreview;
    
    // Validate custom slug format
    if (customSlug.trim()) {
      if (!slugValidation.isValid) {
        toast.error(slugValidation.message);
        return;
      }
    }
    
    onSubmit({
      name,
      language,
      rank: parseInt(rank, 10) || 0,
      slug: finalSlug,
      page_title: pageTitle,
      meta_keywords: metaKeywords,
      meta_description: metaDescription
    });
    setName('');
    setLanguage('english');
    setRank(0);
    setCustomSlug('');
    setPageTitle('');
    setMetaKeywords('');
    setMetaDescription('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <h2>{title}</h2>
        <form onSubmit={handleSubmit}>

          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Category Name"
            className={styles.modalInput}
            style={{ width: '90%' }}
          />
          
          <div className={styles.formGroup} style={{ width: '90%' }}>
            <label htmlFor="slug">Slug:</label>
            <input
              type="text"
              id="slug"
              value={customSlug}
              onChange={(e) => setCustomSlug(e.target.value)}
              placeholder="Enter custom slug or leave empty for auto-generation"
              className={`${styles.modalInput} ${customSlug && !slugValidation.isValid ? styles.inputError : ''}`}
            />
            {customSlug && (
              <small style={{ 
                color: slugValidation.isValid ? '#4caf50' : '#f44336', 
                fontSize: '12px', 
                marginTop: '4px',
                display: 'block'
              }}>
                {slugValidation.message}
              </small>
            )}
            {name && customSlug !== slugPreview && (
              <small style={{ color: '#666', fontSize: '12px', marginTop: '4px' }}>
                Auto-generated: {slugPreview}
              </small>
            )}
          </div>
      
          <div className={styles.formGroup} style={{ width: '95%' }}>
            <label htmlFor="language">Language:</label>
            <select
              id="language"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={styles.modalSelect}
            >
              {LANGUAGES.map(lang => (
                <option key={lang.value} value={lang.value}>{lang.label}</option>
              ))}
            </select>
          </div>
    
          <div className={styles.formGroup} style={{ width: '90%' }}>
            <label htmlFor="rank">Display Order (Rank):</label>
            <input
              type="number"
              id="rank"
              value={rank}
              onChange={(e) => setRank(e.target.value)}
              placeholder="Rank (lower numbers appear first)"
              className={styles.modalInput}
              min="0"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="page_title">Page Title</label>
            <textarea
              id="page_title"
              name="page_title"
              value={pageTitle}
              onChange={(e) => setPageTitle(e.target.value)}
              rows="2"
              placeholder="SEO Page Title"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="meta_keywords">Meta Keywords</label>
            <textarea
              id="meta_keywords"
              name="meta_keywords"
              value={metaKeywords}
              onChange={(e) => setMetaKeywords(e.target.value)}
              rows="2"
              placeholder="SEO Meta Keywords"
            />
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="meta_description">Meta Description</label>
            <textarea
              id="meta_description"
              name="meta_description"
              value={metaDescription}
              onChange={(e) => setMetaDescription(e.target.value)}
              rows="2"
              placeholder="SEO Meta Description"
              className='form-control'
            />
          </div>
          
          <div className={styles.modalButtons}>
            <button type="submit" className={styles.addButton}>
              {title === 'Add Category' ? 'Add' : 'Update'}
            </button>
            <button type="button" onClick={onClose} className={styles.cancelButton}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const DeleteModal = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <h2>Confirm Delete</h2>
        <p>Are you sure you want to delete this category?</p>
        <div className={styles.modalButtons}>
          <button onClick={onConfirm} className={styles.deleteButton}>
            Delete
          </button>
          <button onClick={onClose} className={styles.cancelButton}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const CategoriesView = () => {
  const { category } = useParams();
  const [selectedLanguage, setSelectedLanguage] = useState('english');
  
  const { 
    categories, 
    isLoading, 
    error, 
    createCategory, 
    updateCategory, 
    deleteCategory,
    updateCategoryRank
  } = useCategories(category, selectedLanguage);

  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleAdd = async ({ name, language, rank, slug, page_title, meta_keywords, meta_description }) => {
    try {
      await createCategory({ name, language, rank, slug, page_title, meta_keywords, meta_description });
      toast.success('Category added successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to add category');
    }
  };

  const handleEdit = async ({ name, language, rank, slug, page_title, meta_keywords, meta_description }) => {
    try {
      await updateCategory({
        categoryId: selectedCategory.id,
        name,
        language,
        rank,
        slug,
        page_title, 
        meta_keywords, 
        meta_description
      });
      setIsEditModalOpen(false);
      toast.success('Category updated successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update category');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteCategory(selectedCategory.id);
      setIsDeleteModalOpen(false);
      toast.success('Category deleted successfully!');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to delete category');
    }
  };

  const handleMoveUp = async (category) => {
    try {
      const currentIndex = filteredCategories.findIndex(c => c.id === category.id);
      if (currentIndex > 0) {
        const prevCategory = filteredCategories[currentIndex - 1];
        const newRank = prevCategory.rank !== null ? prevCategory.rank - 1 : 0;
        await updateCategoryRank(category.id, newRank);
        toast.success('Category moved up successfully!');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update category rank');
    }
  };

  const handleMoveDown = async (category) => {
    try {
      const currentIndex = filteredCategories.findIndex(c => c.id === category.id);
      if (currentIndex < filteredCategories.length - 1) {
        const nextCategory = filteredCategories[currentIndex + 1];
        const newRank = nextCategory.rank !== null ? nextCategory.rank + 1 : filteredCategories.length;
        await updateCategoryRank(category.id, newRank);
        toast.success('Category moved down successfully!');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to update category rank');
    }
  };

  const filteredCategories = categories?.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  if (isLoading) {
    return <div className={styles.loading}><Loading/></div>;
  }

  if (error) {
    return <div className={styles.error}>Error: {error}</div>;
  }

  return (
    <div className={styles.container}>
      <h2 style={{ color:'white' }}>{category?.charAt(0).toUpperCase() + category?.slice(1)} Categories</h2>
      
      <div className={styles.header}>
        <div className={styles.headerActions}>
          <button
            className={styles.addButton}
            onClick={() => setIsAddModalOpen(true)}
          >
            <Plus /> Add Category
          </button>
          
          <div className={styles.languageFilter}>
            <Globe className={styles.globeIcon} />
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className={styles.languageSelect}
            >
              {LANGUAGES.map(lang => (
                <option key={lang.value} value={lang.value}>{lang.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className={styles.searchBar}>
          <Search className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search categories..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className={styles.categoriesList}>
        {filteredCategories.length === 0 ? (
          <div className={styles.noCategories}>
            {searchTerm 
              ? 'No categories found' 
              : `No ${selectedLanguage} categories available for ${category}`}
          </div>
        ) : (
          filteredCategories.map((cat, index) => (
            <div key={cat.id} className={styles.categoryItem}>
              <div className={styles.categoryInfo}>
                <span className={styles.categoryName}>{cat.name}</span>
                <span className={styles.categoryMeta}>
                  <span className={styles.categoryLanguage}>{cat.language}</span>
                  {cat.slug && (
                    <span className={styles.categorySlug}>Slug: {cat.slug}</span>
                  )}
                  {cat.rank !== null && (
                    <span className={styles.categoryRank}>Rank: {cat.rank}</span>
                  )}
                </span>
              </div>
              <div className={styles.actions}>
                <button
                  className={styles.iconButton}
                  onClick={() => handleMoveUp(cat)}
                  disabled={index === 0}
                  title="Move Up"
                >
                  <ArrowUp />
                </button>
                <button
                  className={styles.iconButton}
                  onClick={() => handleMoveDown(cat)}
                  disabled={index === filteredCategories.length - 1}
                  title="Move Down"
                >
                  <ArrowDown />
                </button>
                <button
                  className={styles.iconButton}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setIsEditModalOpen(true);
                  }}
                  title="Edit"
                >
                  <Edit />
                </button>
                <button
                  className={styles.iconButton}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setIsDeleteModalOpen(true);
                  }}
                  title="Delete"
                >
                  <Trash />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <CategoryModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Category"
        categoryData={{ name: '', language: selectedLanguage, rank: filteredCategories.length }}
        onSubmit={handleAdd}
      />

      <CategoryModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Category"
        categoryData={selectedCategory}
        onSubmit={handleEdit}
      />

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default CategoriesView;